import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faChartLine,
    faInfoCircle,
    faCheckCircle,
    faHourglassHalf,
    faTimesCircle,
    faTrophy,
    faExclamationTriangle,
    faChevronDown,
    faChevronUp,
    faUser,
    faUsers,
    faBan,
    faDownload,
    faSpinner,
    faFileInvoiceDollar,
    faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import AdjustmentDetailModal from '../../components/AdjustmentDetailModal';

// Proof-of-payment files for one batch line (CT view, confirmed batches only).
const PaymentProofSection = ({ batchId, lineId }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        api.get(`/payments/${batchId}/lines/${lineId}/proof-files`)
            .then(res => { if (active) setFiles(res.data?.files || []); })
            .catch(() => { if (active) setFiles([]); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [batchId, lineId]);

    const download = async (file) => {
        try {
            setDownloading(file.id);
            const res = await api.get(`/payments/proof-files/${file.id}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = file.original_filename || `preuve-${file.id}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            // ignore — download failure is surfaced by the empty file
        } finally {
            setDownloading(null);
        }
    };

    if (loading) return <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-400">Chargement des preuves de paiement…</div>;
    if (files.length === 0) {
        return (
            <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-400">
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />Aucune preuve de paiement téléversée.
            </div>
        );
    }

    return (
        <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-green-600" />
                Preuve de paiement
            </h4>
            <div className="flex flex-col gap-2">
                {files.map(f => (
                    <div key={f.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                            <div className="text-sm text-gray-800 truncate">{f.original_filename}</div>
                            <div className="text-xs text-gray-400">{f.file_size}{f.uploaded_at ? ` · ${f.uploaded_at}` : ''}{f.uploaded_by ? ` · ${f.uploaded_by}` : ''}</div>
                        </div>
                        <button
                            onClick={() => download(f)}
                            disabled={downloading === f.id}
                            className="ml-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                        >
                            <FontAwesomeIcon icon={downloading === f.id ? faSpinner : faDownload} className={downloading === f.id ? 'animate-spin' : ''} />
                            Télécharger
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SupervisorPayments = ({ supervisorId, embedded = false, canOpenBatch = false } = {}) => {
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]);
    const [statistics, setStatistics] = useState({
        total_payments: 0,
        total_paid: 0,
        pending_amount: 0,
        total_completes: 0,
        total_bonuses: 0,
        total_penalties: 0,
        total_carryover: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Retenue de garantie per survey (held until the chef terrain marks the work finished)
    const [retenueGuarantees, setRetenueGuarantees] = useState([]);
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        fetchPayments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supervisorId]);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            setError('');
            // CT/admin viewing a specific prestataire vs. a supervisor viewing their own.
            const url = supervisorId ? `/supervisors/${supervisorId}/payments` : '/f2f/supervisor/payments';
            const response = await api.get(url);
            if (response.data.success) {
                setPayments(response.data.data.payments);
                setStatistics(response.data.data.statistics);
                setRetenueGuarantees(response.data.data.retenue_guarantees || []);
            } else {
                setError(response.data.message || 'Erreur lors du chargement des paiements');
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
            setError('Erreur lors du chargement de l\'historique des paiements');
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (paymentId) => {
        setExpandedRows(prev => ({ ...prev, [paymentId]: !prev[paymentId] }));
    };

    const getStatusBadge = (payment) => {
        if (payment.payment_status === 'excluded') {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <FontAwesomeIcon icon={faBan} className="mr-1" />
                    Exclu
                </span>
            );
        }

        // Cancelled (annulé) advance — a rejected line. Terminal: takes priority over batch status
        // so a confirmed batch never shows a cancelled line as "Traité".
        if (payment.payment_status === 'rejected') {
            return (
                <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                    title={payment.payment_notes || 'Avance annulée'}
                >
                    <FontAwesomeIcon icon={faBan} className="mr-1" />
                    Annulé
                </span>
            );
        }

        // Reported (unpaid/insufficient) — the amount was NOT paid and is carried over to the next
        // batch. Must take priority over batch status so a confirmed batch doesn't show it as "Traité".
        if (payment.payment_status === 'unpaid' || payment.payment_status === 'insufficient') {
            return (
                <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                    title={payment.payment_notes || 'Montant reporté au prochain lot'}
                >
                    <FontAwesomeIcon icon={faHourglassHalf} className="mr-1" />
                    Reporté
                </span>
            );
        }

        const statusMap = {
            'draft': { label: 'En Préparation', color: 'bg-yellow-100 text-yellow-700', icon: faHourglassHalf },
            'confirmed': { label: 'Traité', color: 'bg-green-100 text-green-700', icon: faCheckCircle },
            'validated': { label: 'Traité', color: 'bg-green-100 text-green-700', icon: faCheckCircle },
            'locked': { label: 'En Vérification', color: 'bg-blue-100 text-blue-700', icon: faInfoCircle },
            'exported': { label: 'Traité', color: 'bg-green-100 text-green-700', icon: faCheckCircle }
        };
        const statusInfo = statusMap[payment.status] || statusMap['draft'];
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                <FontAwesomeIcon icon={statusInfo.icon} className="mr-1" />
                {statusInfo.label}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatPeriod = (start, end) => {
        if (!start || !end) return '-';
        const s = new Date(start).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const e = new Date(end).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return `${s} — ${e}`;
    };

    const handleAdjustmentClick = (adjustment, payment) => {
        const adjustmentWithContext = {
            ...adjustment,
            batch_ref: payment.batch_ref,
            created_at: adjustment.at || payment.created_at
        };
        setSelectedAdjustment(adjustmentWithContext);
        setShowAdjustmentModal(true);
    };

    if (loading) {
        return (
            <div className={embedded ? 'flex items-center justify-center py-16' : 'min-h-screen bg-gray-50 flex items-center justify-center'}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement de l'historique des paiements...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={embedded ? 'flex items-center justify-center py-16' : 'min-h-screen bg-gray-50 flex items-center justify-center'}>
                <div className="text-center">
                    <FontAwesomeIcon icon={faTimesCircle} className="text-5xl text-red-600 mb-4" />
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={embedded ? '' : 'min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8'}>
            <div className={embedded ? '' : 'max-w-7xl mx-auto'}>
                {/* Header */}
                {!embedded && (
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                        Historique des Paiements
                    </h1>
                    <p className="text-gray-600">Consultez vos lots de paiement Face à Face avec les détails par enquête</p>
                </div>
                )}

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total des réalisations</h3>
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-2xl text-green-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_realisations ?? statistics.total_paid)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Paiements des réalisations reçus (hors frais)</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Frais de déplacement</h3>
                            <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-2xl text-purple-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_frais_deplacement || 0)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Avances de frais reçues</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Complétés</h3>
                            <FontAwesomeIcon icon={faChartLine} className="text-2xl text-blue-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {statistics.total_completes || 0}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Enquêtes complétées</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Bonus</h3>
                            <FontAwesomeIcon icon={faTrophy} className="text-2xl text-green-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_bonuses || 0)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Récompenses cumulées</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Pénalités</h3>
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-red-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_penalties || 0)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Ajustements négatifs</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total des Montants Reportés</h3>
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-2xl text-purple-600" />
                        </div>
                        <div className="text-3xl font-bold text-purple-600">
                            +{formatCurrency(statistics.total_carryover || 0)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Montants reportés des lots précédents</p>
                    </div>
                </div>

                {/* Retenues de garantie */}
                {retenueGuarantees.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-yellow-200 mb-8">
                        <div className="p-6 border-b border-yellow-100">
                            <h2 className="text-xl font-bold text-gray-900">Retenues de garantie</h2>
                            <p className="text-sm text-gray-500 mt-1">Une partie de votre travail est retenue chaque semaine en garantie. Elle vous est payée en une fois quand le chef terrain marque le travail de l'enquête comme terminé.</p>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {retenueGuarantees.map((g) => (
                                <div key={g.survey_id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="font-medium text-gray-900">{g.survey_title}</div>
                                    <div className="flex flex-wrap gap-2 text-sm">
                                        {g.held > 0 && <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">Retenu : {formatCurrency(g.held)}</span>}
                                        {g.released > 0 && <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">Libéré, payé au prochain lot : {formatCurrency(g.released)}</span>}
                                        {g.paid > 0 && <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">Payé : {formatCurrency(g.paid)}</span>}
                                        {g.forfeited > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">Non payé (travail abandonné) : {formatCurrency(g.forfeited)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Payments Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">Détails des Paiements</h2>
                    </div>

                    {payments.length === 0 ? (
                        <div className="text-center py-12">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-5xl text-gray-300 mb-4" />
                            <p className="text-gray-500">Aucun lot de paiement disponible pour le moment</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Lot
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Période
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quantité
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Brut
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ajustements
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Net
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Statut
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {payments.map((payment) => {
                                        const isExcluded = payment.payment_status === 'excluded';
                                        const isCancelled = payment.payment_status === 'rejected';
                                        const isVoided = isExcluded || isCancelled;
                                        // Retenue (F2F): only a % of the survey is withheld — the survey stays paid, not struck through
                                        const retenueBySurvey = {};
                                        (payment.adjustments || []).forEach(adj => {
                                            if (adj.type === 'survey_exclusion' && adj.kind === 'retenue') retenueBySurvey[String(adj.survey_id)] = adj;
                                        });
                                        const excludedSurveyIds = (payment.excluded_surveys || []).map(String).filter(id => !retenueBySurvey[id]);
                                        const isPartiallyExcluded = !isExcluded && (excludedSurveyIds.length > 0 || Object.keys(retenueBySurvey).length > 0);
                                        return (
                                        <React.Fragment key={payment.id}>
                                            <tr
                                                className={`hover:bg-gray-50 transition-colors cursor-pointer ${isVoided ? 'opacity-60' : ''}`}
                                                onClick={() => toggleRow(payment.id)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {canOpenBatch && payment.batch_id ? (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/payments/${payment.batch_id}`); }}
                                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:underline"
                                                            title="Ouvrir le lot de paiement"
                                                        >
                                                            {payment.batch_ref}
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                                                        </button>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {payment.batch_ref}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-900">
                                                        {formatPeriod(payment.period_start, payment.period_end)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {payment.is_advance_only ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                            Frais de déplacement
                                                        </span>
                                                    ) : (
                                                        <span className={`text-sm ${isVoided ? 'line-through text-gray-400' : 'text-gray-900'}`}>{payment.qty}</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`text-sm font-medium ${isVoided ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                        {formatCurrency(payment.total)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                                    {payment.adjustments && payment.adjustments.length > 0 ? (
                                                        <div className="space-y-2">
                                                            {payment.adjustments.map((adj, idx) => (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => handleAdjustmentClick(adj, payment)}
                                                                    className={`block text-sm font-medium hover:underline transition-colors duration-200 ${
                                                                        adj.kind === 'retenue' ? 'text-yellow-700 hover:text-yellow-800'
                                                                        : adj.amount > 0 ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'
                                                                    }`}
                                                                >
                                                                    {adj.amount > 0 ? '+' : ''}{formatCurrency(adj.amount)}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`text-sm font-bold ${isVoided ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                        {formatCurrency(payment.net)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(payment)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-500">
                                                        {formatDate(payment.created_at)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <FontAwesomeIcon
                                                        icon={expandedRows[payment.id] ? faChevronUp : faChevronDown}
                                                        className="text-gray-400"
                                                    />
                                                </td>
                                            </tr>
                                            {/* Exclusion reason row */}
                                            {isExcluded && payment.exclusion_reason && (
                                                <tr>
                                                    <td colSpan={9} className="px-6 py-2 bg-amber-50 border-b border-amber-100">
                                                        <div className="flex items-center gap-2 text-sm text-amber-700">
                                                            <FontAwesomeIcon icon={faBan} className="text-amber-500" />
                                                            <span>Raison : {payment.exclusion_reason}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {/* Cancellation (annulé) reason row */}
                                            {isCancelled && (
                                                <tr>
                                                    <td colSpan={9} className="px-6 py-2 bg-red-50 border-b border-red-100">
                                                        <div className="flex items-center gap-2 text-sm text-red-700">
                                                            <FontAwesomeIcon icon={faBan} className="text-red-500" />
                                                            <span>Annulée — Raison : {payment.payment_notes || 'Non renseignée'}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {/* Expanded Detail Row */}
                                            {expandedRows[payment.id] && (
                                                <tr>
                                                    <td colSpan={9} className="px-6 py-4 bg-gray-50">
                                                        {/* Work Attribution */}
                                                        <div className="mb-4 flex flex-wrap gap-3">
                                                            {(payment.supervisor_own_qty > 0) && (
                                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-sm">
                                                                    <FontAwesomeIcon icon={faUser} className="text-gray-500" />
                                                                    <span className="text-gray-700">
                                                                        Vous : <strong>{payment.supervisor_own_qty}</strong> complétés ({formatCurrency(payment.supervisor_own_amount)})
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {(payment.interviewer_qty > 0) && (
                                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                                                                    <FontAwesomeIcon icon={faUsers} className="text-gray-400" />
                                                                    <span className="text-gray-600">
                                                                        Vos enquêteurs ({payment.interviewer_count}) : <strong>{payment.interviewer_qty}</strong> complétés ({formatCurrency(payment.interviewer_amount)})
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {(payment.supervisor_own_qty === 0 && payment.interviewer_qty === 0) && (
                                                                <div className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm ${payment.is_advance_only ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>
                                                                    {payment.is_advance_only ? 'Frais de déplacement' : 'Report de montant uniquement'}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Frais de déplacement: the survey it concerns */}
                                                        {payment.is_advance_only && payment.advance_survey_title && (
                                                            <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-purple-200 rounded-lg text-sm">
                                                                <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-purple-500" />
                                                                <span className="text-gray-700">Enquête concernée : <strong>{payment.advance_survey_title}</strong></span>
                                                            </div>
                                                        )}

                                                        {/* Per-survey breakdown */}
                                                        {payment.surveys && payment.surveys.length > 0 && (
                                                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                                                                <thead className="bg-gray-100">
                                                                    <tr>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enquête</th>
                                                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qté</th>
                                                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">PPC</th>
                                                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-100">
                                                                    {payment.surveys.map((survey, idx) => {
                                                                        const isSurveyExcluded = excludedSurveyIds.includes(String(survey.survey_id));
                                                                        const supervisorOwnQty = survey.supervisor_own_qty ?? 0;
                                                                        const surveyInterviewers = survey.interviewers || [];
                                                                        const hasBreakdown = supervisorOwnQty > 0 || surveyInterviewers.length > 0;
                                                                        return (
                                                                        <React.Fragment key={idx}>
                                                                        <tr className={`bg-white ${isSurveyExcluded ? 'opacity-60' : ''}`}>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">
                                                                                {survey.survey_title}
                                                                                {isSurveyExcluded && (
                                                                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                                                                        <FontAwesomeIcon icon={faBan} className="mr-1" />
                                                                                        Exclue
                                                                                    </span>
                                                                                )}
                                                                                {retenueBySurvey[String(survey.survey_id)] && (
                                                                                    <span
                                                                                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-white border border-yellow-400 text-yellow-700"
                                                                                        title={retenueBySurvey[String(survey.survey_id)].reason || undefined}
                                                                                    >
                                                                                        Retenue {Number(retenueBySurvey[String(survey.survey_id)].percentage || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} % : −{formatCurrency(Math.abs(retenueBySurvey[String(survey.survey_id)].amount))} (gardée en garantie)
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className={`px-4 py-2 text-sm text-right ${isSurveyExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>{survey.qty}</td>
                                                                            <td className={`px-4 py-2 text-sm text-right ${isSurveyExcluded ? 'text-gray-400' : 'text-gray-900'}`}>{formatCurrency(survey.unit_price)}</td>
                                                                            <td className={`px-4 py-2 text-sm font-medium text-right ${isSurveyExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>{formatCurrency(survey.amount)}</td>
                                                                        </tr>
                                                                        {hasBreakdown && (
                                                                            <tr className="bg-gray-50">
                                                                                <td colSpan={4} className="px-6 py-2">
                                                                                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Détail par personne (complétés)</div>
                                                                                    <div className="flex flex-wrap gap-2">
                                                                                        {supervisorOwnQty > 0 && (
                                                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-medium">
                                                                                                Vous (prestataire) : {supervisorOwnQty}
                                                                                            </span>
                                                                                        )}
                                                                                        {surveyInterviewers.map((it) => (
                                                                                            <span key={it.interviewer_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs">
                                                                                                {it.name}{it.ref ? ` (${it.ref})` : ''} : {it.qty}
                                                                                            </span>
                                                                                        ))}
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                        </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        )}

                                                        {/* Proof of payment — CT view, only once the batch is confirmed/paid */}
                                                        {canOpenBatch && ['confirmed', 'validated', 'exported'].includes(payment.status) && (
                                                            <PaymentProofSection batchId={payment.batch_id} lineId={payment.id} />
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Payment Method Info */}
                {payments.length > 0 && payments[0].payment_method && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-xl mt-1 mr-3" />
                            <div>
                                <h3 className="text-sm font-semibold text-blue-900 mb-2">Méthode de Paiement</h3>
                                <p className="text-sm text-blue-700">
                                    {payments[0].payment_method === 'bank'
                                        ? `Versement bancaire - ${payments[0].bank_name || 'Banque'}`
                                        : `Service de transfert - ${payments[0].transfer_service || 'Service'}`
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Adjustment Detail Modal */}
            <AdjustmentDetailModal
                isOpen={showAdjustmentModal}
                onClose={() => {
                    setShowAdjustmentModal(false);
                    setSelectedAdjustment(null);
                }}
                adjustment={selectedAdjustment}
                showAdminInfo={false}
            />
        </div>
    );
};

export default SupervisorPayments;
